import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, Package, MessageCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

const WHATS = "https://wa.me/5519992923017?text=Ol%C3%A1%20Biofarmax!";

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: "Início" },
    { to: "/loja", label: "Loja" },
    { to: "/categoria/biobeauty", label: "Biobeauty" },
    { to: "/categoria/saude", label: "Saúde" },
    { to: "/categoria/bem-estar", label: "Bem-Estar" },
    { to: "/categoria/fitness", label: "Fitness" },
    { to: "/sobre", label: "Sobre" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass border-b border-white/30 shadow-[0_8px_32px_-12px_rgba(15,15,15,0.12)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-header"
    >
      <div className={`max-w-7xl mx-auto px-5 md:px-10 ${scrolled ? "py-1 sm:py-2" : "py-2 sm:py-3"} flex items-center justify-between gap-4 transition-all duration-300`}>
        <Link
          to="/"
          className="flex items-center gap-3 text-brand-primary"
          data-testid="logo-link"
        >
          <img
            src="/assets/biofarmax-logo.png"
            alt="Biofarmax · Farmácia de Manipulação"
            className="h-12 sm:h-14 lg:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 font-tech text-sm">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `transition-colors hover:text-brand-accent ${
                  isActive ? "text-brand-accent" : "text-ink-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={WHATS}
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full font-tech text-sm bg-[#25D366]/10 text-[#1B7A3D] border border-[#25D366]/30 hover:bg-[#25D366] hover:text-white transition"
            data-testid="header-whatsapp"
          >
            <MessageCircle size={16} strokeWidth={1.8} />
            <span className="hidden lg:inline">WhatsApp</span>
          </a>
          <button
            onClick={() => setOpen(true)}
            className="relative p-2 hover:text-brand-accent transition"
            data-testid="cart-toggle-btn"
            aria-label="Carrinho"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-brand-accent text-white text-[10px] font-tech font-semibold rounded-full w-5 h-5 flex items-center justify-center"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenu(!userMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-bg-tertiary transition"
                data-testid="user-menu-btn"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-brand-primary text-white grid place-items-center text-xs font-tech">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <span className="hidden md:block text-sm font-tech">{user.name?.split(" ")[0]}</span>
              </button>
              {userMenu && (
                <div
                  className="absolute right-0 top-14 w-56 bg-white rounded-2xl shadow-hover border border-black/5 overflow-hidden"
                  onMouseLeave={() => setUserMenu(false)}
                >
                  <div className="px-4 py-3 border-b border-black/5">
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="text-xs text-ink-secondary truncate">{user.email}</div>
                  </div>
                  <Link
                    to="/conta"
                    onClick={() => setUserMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-bg-secondary text-sm"
                    data-testid="account-link"
                  >
                    <Package size={16} /> Meus pedidos
                  </Link>
                  {user.roles?.includes("admin") && (
                    <Link
                      to="/admin"
                      onClick={() => setUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-bg-secondary text-sm"
                      data-testid="admin-link"
                    >
                      <LayoutDashboard size={16} /> Admin
                    </Link>
                  )}
                  <button
                    onClick={async () => {
                      await logout();
                      setUserMenu(false);
                      nav("/");
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-secondary text-sm text-left text-status-error"
                    data-testid="logout-btn"
                  >
                    <LogOut size={16} /> Sair
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full font-tech text-sm hover:bg-bg-tertiary"
              data-testid="login-link"
            >
              <User size={18} strokeWidth={1.5} /> Entrar
            </Link>
          )}

          <button
            className="lg:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden glass border-t border-white/30">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `py-3 font-tech ${isActive ? "text-brand-accent" : ""}`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            {!user && (
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="py-3 font-tech text-brand-primary"
              >
                Entrar / Cadastrar
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
